<script setup lang="ts">
  import { computed } from 'vue';
  import { ILogicalPrinter } from '@/stores/printing-v2.config';
  import {
    formatJobTime,
    jobStateUi,
    printerStatusUi,
    transportIcon,
    transportLabelKey,
    useV2PrintingUi,
  } from '@/composition/printing/use-printing-v2-ui';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';

  const props = defineProps<{
    logical: ILogicalPrinter;
    disabled?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'test'): void;
    (e: 'edit'): void;
    (e: 'remove'): void;
  }>();

  const { bridgePrinterByRef, assignmentsOf, lastJobOf } = useV2PrintingUi();

  const bridgePrinter = computed(() =>
    bridgePrinterByRef.value.get(props.logical.ref)
  );
  const statusUi = computed(() => printerStatusUi(bridgePrinter.value));
  const assignments = computed(() => assignmentsOf(props.logical.id));
  const lastJob = computed(() => lastJobOf(props.logical.ref));
  const lastJobUi = computed(() =>
    lastJob.value ? jobStateUi(lastJob.value.state) : null
  );
  const lastJobTime = computed(() =>
    lastJob.value ? formatJobTime(lastJob.value.acceptedAt) : ''
  );
</script>

<template>
  <div class="v2-printer-card" :class="{ 'is-disabled': disabled }">
    <div class="card-top">
      <div class="card-transport-icon">
        <PosUiIcon :name="transportIcon(logical.transport)" :size="22" />
      </div>

      <div class="card-heading">
        <div class="card-name">{{ logical.name }}</div>
        <div class="card-subtitle">
          {{ $t(transportLabelKey(logical.transport)) }}
          <template v-if="logical.format === 'escpos'">
            · {{ logical.paperWidthMm }} mm
          </template>
          <template v-else>· {{ $t('printingV2.destSticker') }}</template>
        </div>
      </div>

      <v-menu
        v-if="!disabled"
        offset-y
        left
        content-class="v2-printer-card-menu"
      >
        <template #activator="{ on, attrs }">
          <button
            class="card-menu-button"
            type="button"
            v-bind="attrs"
            v-on="on"
          >
            <PosUiIcon name="dots-vertical" :size="20" />
          </button>
        </template>
        <div class="menu-panel">
          <button class="menu-item" type="button" @click="emit('test')">
            <PosUiIcon name="printer-check" class="menu-item-icon" />
            {{ $t('printingV2.menuTest') }}
          </button>
          <button class="menu-item" type="button" @click="emit('edit')">
            <PosUiIcon name="pencil" class="menu-item-icon" />
            {{ $t('printingV2.menuEdit') }}
          </button>
          <button
            class="menu-item is-danger"
            type="button"
            @click="emit('remove')"
          >
            <PosUiIcon name="delete-outline" class="menu-item-icon" />
            {{ $t('printingV2.menuRemove') }}
          </button>
        </div>
      </v-menu>
    </div>

    <div class="card-badges">
      <PosUiBadge :color="statusUi.color" variant="soft" size="sm">
        {{ $t(statusUi.labelKey) }}
      </PosUiBadge>
      <PosUiBadge
        v-for="assignment in assignments"
        :key="assignment.key"
        color="info"
        variant="soft"
        size="sm"
      >
        <template v-if="assignment.key === 'ticket'">
          {{ $t('printingV2.destTicket') }}
        </template>
        <template v-else-if="assignment.key === 'sticker'">
          {{ $t('printingV2.destSticker') }}
        </template>
        <template v-else>{{ assignment.label }}</template>
      </PosUiBadge>
    </div>

    <div v-if="lastJob && lastJobUi" class="card-last-job">
      <PosUiIcon :name="lastJobUi.icon" class="card-last-job-icon" />
      {{ $t('printingV2.lastJob', { when: lastJobTime }) }} —
      {{ $t(lastJobUi.labelKey) }}
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-printer-card {
    @apply tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white tw-p-4 tw-shadow-sm;

    &.is-disabled {
      @apply tw-opacity-60;
    }

    .card-top {
      @apply tw-flex tw-items-start tw-gap-3;

      .card-transport-icon {
        @apply tw-flex tw-h-10 tw-w-10 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-neutral-100 tw-text-neutral-500;
      }

      .card-heading {
        @apply tw-min-w-0 tw-flex-1;

        .card-name {
          @apply tw-truncate tw-text-sm tw-font-semibold tw-text-neutral-900;
        }

        .card-subtitle {
          @apply tw-text-xs tw-text-neutral-500;
        }
      }

      .card-menu-button {
        @apply tw-flex tw-h-8 tw-w-8 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-text-neutral-400 tw-transition-colors hover:tw-bg-neutral-100 hover:tw-text-neutral-700;
      }
    }

    .card-badges {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-1.5;
    }

    .card-last-job {
      @apply tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-neutral-500;

      .card-last-job-icon {
        @apply tw-text-sm;
      }
    }
  }
</style>

<style lang="scss">
  // Not scoped: v-menu teleports its content to the app root.
  .v2-printer-card-menu {
    @apply tw-rounded-xl tw-shadow-lg;

    .menu-panel {
      @apply tw-flex tw-min-w-[180px] tw-flex-col tw-bg-white tw-p-1;

      .menu-item {
        @apply tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-px-3 tw-py-2.5 tw-text-left tw-text-sm tw-text-neutral-700 tw-transition-colors hover:tw-bg-neutral-50;

        .menu-item-icon {
          @apply tw-text-lg tw-text-neutral-400;
        }

        &.is-danger {
          @apply tw-text-red-600;

          .menu-item-icon {
            @apply tw-text-red-400;
          }
        }
      }
    }
  }
</style>
